import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSets, useDeleteSet } from '@/hooks/useSets';
import { useTasks } from '@/hooks/useTasks';
import { GENERAL_SET_ID, GENERAL_SET } from '@/constants/sets';
import EditSetModal from './EditSetModal';
import TaskListItem from '@/components/tasks/TaskListItem';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import EmptyState from '@/components/common/EmptyState';
import Modal from '@/components/common/Modal';
import Spinner from '@/components/common/Spinner';
import { formatRelativeTime } from '@/utils/formatRelativeTime';

/**
 * Detail view for the virtual "General" set.
 * Shows all active moves that are not assigned to any real set.
 */
function GeneralSetDetail() {
  const navigate = useNavigate();
  const { data: tasksData, isLoading: tasksLoading, error: tasksError } = useTasks('active');

  // Filter for unassigned tasks client-side
  const unassignedTasks = tasksData?.tasks.filter((task) => task.set_id === null) ?? [];

  return (
    <div className="mx-auto max-w-3xl p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-900">{GENERAL_SET.name}</h1>
      </div>

      {/* Description */}
      <section className="mb-8 rounded-xl bg-gradient-to-br from-neutral-200 to-neutral-300 border border-neutral-400/15 shadow-md shadow-black/30 p-5">
        <h2 className="mb-2 text-sm font-semibold text-neutral-600">Description</h2>
        <p className="text-sm text-neutral-700 whitespace-pre-wrap">
          {GENERAL_SET.description}
        </p>
      </section>

      {/* Moves in this Set */}
      <section className="rounded-xl bg-gradient-to-br from-neutral-200 to-neutral-300 border border-neutral-400/15 shadow-md shadow-black/30 p-5">
        <h2 className="mb-4 text-sm font-semibold text-neutral-600">Moves in this Set</h2>

        {tasksLoading && <Spinner className="py-8" />}

        {tasksError && (
          <div className="p-4 text-center text-sm text-error-600">Failed to load moves</div>
        )}

        {!tasksLoading && !tasksError && unassignedTasks.length === 0 && (
          <EmptyState title="No active moves" description="There are no active moves in this set" />
        )}

        <div className="space-y-2">
          {unassignedTasks.map((task) => (
            <TaskListItem
              key={task.task_id}
              task={task}
              onClick={() => navigate(`/app/tasks/${task.task_id}`)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

export default function SetDetail() {
  const { id: setId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // All hooks must be called unconditionally (Rules of Hooks)
  const { data: setsData, isLoading } = useSets('active');
  const deleteSet = useDeleteSet();
  const { data: tasksData, isLoading: tasksLoading, error: tasksError } = useTasks('active', setId);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // If viewing the General virtual set, render the dedicated component
  if (setId === GENERAL_SET_ID) {
    return <GeneralSetDetail />;
  }

  // Find the set from the cached sets data
  const set = setsData?.sets.find((s) => s.set_id === setId);

  if (isLoading) return <Spinner className="py-12" />;

  if (!set) {
    return (
      <div className="p-8 text-center text-sm text-error-600">
        Set not found
      </div>
    );
  }

  const handleDelete = async () => {
    if (!setId) return;
    try {
      await deleteSet.mutateAsync(setId);
      setShowDeleteConfirm(false);
      navigate('/app/sets');
    } catch {
      // Error handled by mutation state
    }
  };

  return (
    <div className="mx-auto max-w-3xl p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-2">
          {set.is_default && (
            <Badge variant="default">Default</Badge>
          )}
        </div>
        <h1 className="text-2xl font-bold text-neutral-900">{set.name}</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Created {formatRelativeTime(set.created_at)}
        </p>
      </div>

      {/* Description */}
      <section className="mb-8 rounded-xl bg-gradient-to-br from-neutral-200 to-neutral-300 border border-neutral-400/15 shadow-md shadow-black/30 p-5">
        <h2 className="mb-2 text-sm font-semibold text-neutral-600">Description</h2>
        <p className="text-sm text-neutral-700 whitespace-pre-wrap">
          {set.description}
        </p>
      </section>

      {/* Actions — hidden for default set */}
      {!set.is_default && (
        <div className="mb-8 flex gap-3">
          <Button
            variant="secondary"
            onClick={() => setShowEditModal(true)}
          >
            Edit Set
          </Button>
          <Button
            variant="ghost"
            onClick={() => setShowDeleteConfirm(true)}
            className="text-error-600 hover:bg-error-50 hover:text-error-700"
          >
            Delete Set
          </Button>
        </div>
      )}

      {/* Moves in this Set */}
      <section className="rounded-xl bg-gradient-to-br from-neutral-200 to-neutral-300 border border-neutral-400/15 shadow-md shadow-black/30 p-5">
        <h2 className="mb-4 text-sm font-semibold text-neutral-600">Moves in this Set</h2>

        {tasksLoading && <Spinner className="py-8" />}

        {tasksError && (
          <div className="p-4 text-center text-sm text-error-600">Failed to load moves</div>
        )}

        {tasksData && tasksData.tasks.length === 0 && (
          <EmptyState title="No active moves" description="There are no active moves in this set" />
        )}

        <div className="space-y-2">
          {tasksData?.tasks.map((task) => (
            <TaskListItem
              key={task.task_id}
              task={task}
              onClick={() => navigate(`/app/tasks/${task.task_id}`)}
            />
          ))}
        </div>
      </section>

      {/* Edit Modal */}
      <EditSetModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        set={set}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Set"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-700">
            Are you sure you want to delete <strong>"{set.name}"</strong>?
          </p>
          <p className="text-sm text-neutral-500">
            This Set will be archived. Moves in this Set will still be visible
            under "All Moves" in your move list.
          </p>

          {deleteSet.isError && (
            <p className="text-sm text-error-600">
              Failed to delete Set. Please try again.
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleDelete}
              loading={deleteSet.isPending}
              disabled={deleteSet.isPending}
              className="bg-error-600 hover:bg-error-700 focus:ring-error-500"
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
